import { useState } from 'react'
import { downloadFile } from '../api/client'
import {
    useClients,
    useInvoice,
    useInvoiceMutations,
    useInvoices,
    useUnbilled,
} from '../hooks/useApi'
import {
    formatDate,
    formatDurationCompact,
    formatMoney,
    fromLocalInputValue,
} from '../lib/format'
import { useT, type TFunction } from '../i18n/context'
import type { DictPath } from '../i18n/translate'
import { useActiveWorkspaceId, useCanManage } from '../stores/workspace'
import { toast } from '../stores/toast'
import type { Invoice, InvoiceStatus } from '../types/api'
import { Badge } from '../components/ui/Badge'
import { invoiceStatusMeta } from '../lib/labelMaps'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { EmptyState } from '../components/ui/EmptyState'
import { Field, Input, Select, Textarea } from '../components/ui/Field'
import { DateInput } from '../components/ui/DateTimeInputs'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { PageLoader } from '../components/ui/Spinner'
import { errText } from '../lib/errors'

const statusFilterKeys: Record<'' | InvoiceStatus, DictPath | null> = {
    '': null,
    DRAFT: 'invoiceStatus.draft',
    SENT: 'invoiceStatus.sent',
    PAID: 'invoiceStatus.paid',
    CANCELLED: 'invoiceStatus.cancelled',
}

export function InvoicesPage() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const canManage = useCanManage()
    const [status, setStatus] = useState<'' | InvoiceStatus>('')
    const [page, setPage] = useState(1)
    const invoices = useInvoices(workspaceId, status || undefined, page)
    const [detail, setDetail] = useState<Invoice | null>(null)
    const [editing, setEditing] = useState<Invoice | null>(null)
    const [creating, setCreating] = useState(false)

    return (
        <div className='space-y-4'>
            <div className='flex flex-wrap items-center justify-end gap-2'>
                <Select
                    value={status}
                    onChange={e => {
                        setStatus(e.target.value as '' | InvoiceStatus)
                        setPage(1)
                    }}
                    className='w-40'
                >
                    {(Object.keys(statusFilterKeys) as Array<'' | InvoiceStatus>).map(value => (
                        <option key={value} value={value}>
                            {statusFilterKeys[value]
                                ? t(statusFilterKeys[value]!)
                                : t('common.all')}
                        </option>
                    ))}
                </Select>
                <Button onClick={() => setCreating(true)}>
                    {t('invoices.new')}
                </Button>
            </div>

            <Card>
                {invoices.isLoading ? (
                    <PageLoader />
                ) : (invoices.data?.data.length ?? 0) === 0 ? (
                    <EmptyState
                        title={t('invoices.emptyTitle')}
                        description={t('invoices.emptyDesc')}
                        action={
                            <Button onClick={() => setCreating(true)}>
                                {t('invoices.new')}
                            </Button>
                        }
                    />
                ) : (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                    <th className='px-4 py-2.5'>{t('invoices.number')}</th>
                                    <th className='px-4 py-2.5'>{t('invoices.client')}</th>
                                    <th className='px-4 py-2.5'>
                                        {t('invoices.issueDate')}
                                    </th>
                                    <th className='px-4 py-2.5'>{t('invoices.status')}</th>
                                    <th className='px-4 py-2.5 text-right'>
                                        {t('invoices.sum')}
                                    </th>
                                    <th className='px-4 py-2.5' />
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.data!.data.map(inv => (
                                    <tr
                                        key={inv.id}
                                        className='cursor-pointer border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                        onClick={() => setDetail(inv)}
                                    >
                                        <td className='px-4 py-2.5 font-mono font-medium text-slate-800 dark:text-slate-100'>
                                            {inv.number}
                                        </td>
                                        <td className='px-4 py-2.5 text-slate-600 dark:text-slate-300'>
                                            {inv.clientName ?? '—'}
                                        </td>
                                        <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                            {formatDate(inv.issueDate)}
                                        </td>
                                        <td className='px-4 py-2.5'>
                                            <Badge
                                                tone={
                                                    invoiceStatusMeta[
                                                        inv.status
                                                    ].tone
                                                }
                                            >
                                                {t(
                                                    invoiceStatusMeta[
                                                        inv.status
                                                    ].labelKey,
                                                )}
                                            </Badge>
                                        </td>
                                        <td className='px-4 py-2.5 text-right font-semibold tabular-nums text-slate-800 dark:text-slate-100'>
                                            {formatMoney(inv.total)}
                                        </td>
                                        <td
                                            className='px-4 py-2.5 text-right'
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <Button
                                                variant='ghost'
                                                size='sm'
                                                onClick={() =>
                                                    downloadInvoice(inv, t)
                                                }
                                            >
                                                {t('invoices.pdf')}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {invoices.data && invoices.data.meta.totalPages > 1 && (
                    <div className='flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400'>
                        <span>
                            {t('invoices.pageInfo', {
                                page: invoices.data.meta.page,
                                totalPages: invoices.data.meta.totalPages,
                                count: invoices.data.meta.total,
                            })}
                        </span>
                        <div className='flex gap-1'>
                            <Button
                                variant='secondary'
                                size='sm'
                                disabled={page <= 1 || invoices.isFetching}
                                onClick={() => setPage(p => p - 1)}
                            >
                                {t('common.back')}
                            </Button>
                            <Button
                                variant='secondary'
                                size='sm'
                                disabled={
                                    page >= invoices.data.meta.totalPages ||
                                    invoices.isFetching
                                }
                                onClick={() => setPage(p => p + 1)}
                            >
                                {t('common.next')}
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            <InvoiceDetailDialog
                invoice={detail}
                workspaceId={workspaceId!}
                canManage={canManage}
                onEdit={inv => setEditing(inv)}
                onClose={() => setDetail(null)}
            />
            <EditInvoiceDialog
                invoice={editing}
                workspaceId={workspaceId!}
                onClose={() => setEditing(null)}
            />
            <CreateInvoiceDialog
                open={creating}
                workspaceId={workspaceId!}
                onClose={() => setCreating(false)}
            />
        </div>
    )
}

function downloadInvoice(inv: Invoice, t: TFunction) {
    downloadFile(
        `/invoices/${inv.id}/pdf`,
        t('invoices.pdfName', { number: inv.number }),
    ).catch(() => toast.error(t('invoices.pdfFailed')))
}

function InvoiceDetailDialog({
    invoice,
    workspaceId,
    canManage,
    onEdit,
    onClose,
}: {
    invoice: Invoice | null
    workspaceId: number
    canManage: boolean
    onEdit: (invoice: Invoice) => void
    onClose: () => void
}) {
    const t = useT()
    const { updateStatus } = useInvoiceMutations(workspaceId)
    // Detail nachladen — die Liste enthält keine Positionen
    const detail = useInvoice(invoice?.id ?? null)
    const inv = detail.data ?? invoice
    const [statusIntent, setStatusIntent] = useState<{
        status: InvoiceStatus
        statusKey: DictPath
    } | null>(null)

    const setStatus = (status: InvoiceStatus, statusKey: DictPath) => {
        if (!inv) return
        const label = t(statusKey)
        updateStatus.mutate(
            { id: inv.id, status },
            {
                onSuccess: () => {
                    toast.success(t('invoices.statusChanged', { label }))
                    setStatusIntent(null)
                    onClose()
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <Dialog
            open={invoice !== null}
            onClose={onClose}
            title={
                invoice
                    ? t('invoices.detailTitle', { number: invoice.number })
                    : t('invoices.title')
            }
            wide
        >
            {inv && (
                <div className='space-y-4'>
                    <div className='grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm'>
                        <DetailRow
                            label={t('invoices.client')}
                            value={inv.clientName ?? '—'}
                        />
                        <DetailRow
                            label={t('invoices.status')}
                            value={
                                <Badge tone={invoiceStatusMeta[inv.status].tone}>
                                    {t(invoiceStatusMeta[inv.status].labelKey)}
                                </Badge>
                            }
                        />
                        <DetailRow
                            label={t('invoices.issueDate')}
                            value={formatDate(inv.issueDate)}
                        />
                        <DetailRow
                            label={t('invoices.due')}
                            value={
                                inv.dueDate ? formatDate(inv.dueDate) : '—'
                            }
                        />
                        <DetailRow
                            label={t('invoices.servicePeriod')}
                            value={
                                inv.serviceFrom && inv.serviceTo
                                    ? `${formatDate(inv.serviceFrom)} – ${formatDate(inv.serviceTo)}`
                                    : '—'
                            }
                        />
                        <DetailRow
                            label={t('invoices.taxRate')}
                            value={`${inv.taxRate} %`}
                        />
                    </div>

                    {(inv.items ?? []).length > 0 && (
                        <div className='overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                        <th className='px-3 py-2'>{t('invoices.position')}</th>
                                        <th className='px-3 py-2 text-right'>
                                            {t('invoices.hours')}
                                        </th>
                                        <th className='px-3 py-2 text-right'>
                                            {t('invoices.rate')}
                                        </th>
                                        <th className='px-3 py-2 text-right'>
                                            {t('invoices.amount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inv.items!.map(item => (
                                        <tr
                                            key={item.id}
                                            className='border-b border-slate-100 dark:border-slate-800 last:border-0'
                                        >
                                            <td className='px-3 py-2 text-slate-700 dark:text-slate-200'>
                                                {item.description}
                                            </td>
                                            <td className='px-3 py-2 text-right tabular-nums text-slate-600 dark:text-slate-300'>
                                                {formatDurationCompact(
                                                    item.quantity * 3600,
                                                )}
                                            </td>
                                            <td className='px-3 py-2 text-right tabular-nums text-slate-600 dark:text-slate-300'>
                                                {formatMoney(item.unitPrice)}
                                            </td>
                                            <td className='px-3 py-2 text-right font-medium tabular-nums text-slate-800 dark:text-slate-100'>
                                                {formatMoney(item.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className='space-y-1 rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3 text-sm'>
                        <TotalRow
                            label={t('invoices.net')}
                            value={formatMoney(inv.subtotal)}
                        />
                        <TotalRow
                            label={t('invoices.vat', { rate: inv.taxRate })}
                            value={formatMoney(inv.taxAmount)}
                        />
                        <TotalRow
                            label={t('invoices.gross')}
                            value={formatMoney(inv.total)}
                            strong
                        />
                    </div>

                    {inv.note && (
                        <p className='text-sm whitespace-pre-line text-slate-500 dark:text-slate-400'>
                            {inv.note}
                        </p>
                    )}

                    <div className='flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 dark:border-slate-700 pt-3'>
                        <div className='flex flex-wrap gap-2'>
                            <Button
                                variant='secondary'
                                onClick={() => downloadInvoice(inv, t)}
                            >
                                {t('invoices.downloadPdf')}
                            </Button>
                            {inv.status === 'DRAFT' && canManage && (
                                <Button
                                    variant='secondary'
                                    onClick={() => onEdit(inv)}
                                >
                                    {t('common.edit')}
                                </Button>
                            )}
                        </div>
                        <div className='flex flex-wrap gap-2'>
                            {inv.status === 'DRAFT' && (
                                <Button
                                    loading={updateStatus.isPending}
                                    onClick={() =>
                                        setStatusIntent({
                                            status: 'SENT',
                                            statusKey: 'invoiceStatus.sent',
                                        })
                                    }
                                >
                                    {t('invoices.markSent')}
                                </Button>
                            )}
                            {inv.status === 'SENT' && (
                                <Button
                                    loading={updateStatus.isPending}
                                    onClick={() =>
                                        setStatusIntent({
                                            status: 'PAID',
                                            statusKey: 'invoiceStatus.paid',
                                        })
                                    }
                                >
                                    {t('invoices.markPaid')}
                                </Button>
                            )}
                            {inv.status !== 'CANCELLED' &&
                                inv.status !== 'PAID' && (
                                    <Button
                                        variant='secondary'
                                        loading={updateStatus.isPending}
                                        onClick={() =>
                                            setStatusIntent({
                                                status: 'CANCELLED',
                                                statusKey:
                                                    'invoiceStatus.cancelled',
                                            })
                                        }
                                    >
                                        {t('invoices.cancelInvoice')}
                                    </Button>
                                )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={statusIntent !== null}
                message={
                    statusIntent
                        ? t('invoices.setStatusConfirm', {
                              label: t(statusIntent.statusKey),
                          })
                        : ''
                }
                confirmLabel={
                    statusIntent ? t(statusIntent.statusKey) : t('common.confirm')
                }
                tone='primary'
                loading={updateStatus.isPending}
                onClose={() => setStatusIntent(null)}
                onConfirm={() => {
                    if (statusIntent)
                        setStatus(statusIntent.status, statusIntent.statusKey)
                }}
            />
        </Dialog>
    )
}

function DetailRow({
    label,
    value,
}: {
    label: string
    value: React.ReactNode
}) {
    return (
        <div className='flex items-center justify-between gap-2'>
            <span className='text-slate-500 dark:text-slate-400'>{label}</span>
            <span className='font-medium text-slate-700 dark:text-slate-200'>{value}</span>
        </div>
    )
}

function TotalRow({
    label,
    value,
    strong,
}: {
    label: string
    value: string
    strong?: boolean
}) {
    return (
        <div
            className={`flex items-center justify-between ${strong ? 'text-base font-bold text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}
        >
            <span>{label}</span>
            <span className='tabular-nums'>{value}</span>
        </div>
    )
}

/** Draft-Bearbeitung: Datum, Fälligkeit, USt-Satz, Notiz (PATCH /invoices/:id). */
function EditInvoiceDialog({
    invoice,
    workspaceId,
    onClose,
}: {
    invoice: Invoice | null
    workspaceId: number
    onClose: () => void
}) {
    const t = useT()
    const { update } = useInvoiceMutations(workspaceId)
    const [initializedFor, setInitializedFor] = useState<number | null>(null)
    const [issueDate, setIssueDate] = useState('')
    const [dueDate, setDueDate] = useState('')
    const [taxRate, setTaxRate] = useState('')
    const [note, setNote] = useState('')

    // Form-Reset synchron im Render (kein useEffect); Key bei Schluss
    // zurücksetzen, damit dieselbe Rechnung beim nächsten Öffnen frisch lädt.
    if (invoice === null) {
        if (initializedFor !== null) setInitializedFor(null)
    } else if (initializedFor !== invoice.id) {
        setInitializedFor(invoice.id)
        setIssueDate(invoice.issueDate.slice(0, 10))
        setDueDate(invoice.dueDate ? invoice.dueDate.slice(0, 10) : '')
        setTaxRate(String(invoice.taxRate))
        setNote(invoice.note ?? '')
    }

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!invoice) return
        update.mutate(
            {
                id: invoice.id,
                issueDate,
                dueDate: dueDate || null,
                taxRate: Number(taxRate.replace(',', '.')) || 0,
                note: note.trim() || null,
            },
            {
                onSuccess: () => {
                    toast.success(t('invoices.updated'))
                    onClose()
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <Dialog
            open={invoice !== null}
            onClose={onClose}
            title={
                invoice
                    ? t('invoices.editTitle', { number: invoice.number })
                    : t('common.edit')
            }
        >
            <form onSubmit={submit} className='space-y-4'>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                    <Field label={t('invoices.issueDate')}>
                        <DateInput value={issueDate} onChange={setIssueDate} />
                    </Field>
                    <Field label={t('invoices.dueOptional')}>
                        <DateInput value={dueDate} onChange={setDueDate} />
                    </Field>
                    <Field label={t('invoices.vatPct')}>
                        <Input
                            inputMode='decimal'
                            value={taxRate}
                            onChange={e => setTaxRate(e.target.value)}
                            required
                        />
                    </Field>
                </div>
                <Field label={t('invoices.noteOptional')}>
                    <Textarea
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder={t('invoices.notePlaceholder')}
                    />
                </Field>
                <p className='text-xs text-slate-500 dark:text-slate-400'>
                    {t('invoices.draftHint')}
                </p>
                <div className='flex justify-end gap-2'>
                    <Button type='button' variant='secondary' onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button type='submit' loading={update.isPending}>
                        {t('common.save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

function CreateInvoiceDialog({
    open,
    workspaceId,
    onClose,
}: {
    open: boolean
    workspaceId: number
    onClose: () => void
}) {
    const t = useT()
    const clients = useClients(workspaceId)
    const [clientId, setClientId] = useState<number | null>(null)
    const [taxRate, setTaxRate] = useState('20')
    const [note, setNote] = useState('')
    const [dueDate, setDueDate] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const { create } = useInvoiceMutations(workspaceId)
    // Backend vergleicht startTime gte/lte — Tagesgrenzen als volle ISO-Timestamps
    // (lokaler Tag), sonst fällt der Endtag durch UTC-Mitternacht heraus.
    const rangeFrom = from ? fromLocalInputValue(`${from}T00:00`) : undefined
    const rangeTo = to ? fromLocalInputValue(`${to}T23:59`) : undefined
    const unbilled = useUnbilled(workspaceId, clientId, rangeFrom, rangeTo)
    const [createdNumber, setCreatedNumber] = useState<string | null>(null)

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!clientId) return
        create.mutate(
            {
                clientId,
                from: rangeFrom,
                to: rangeTo,
                taxRate: Number(taxRate.replace(',', '.')) || 0,
                dueDate: dueDate || null,
                note: note || undefined,
            },
            {
                onSuccess: inv => {
                    setCreatedNumber(inv.number)
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    const close = () => {
        setClientId(null)
        setTaxRate('20')
        setNote('')
        setDueDate('')
        setFrom('')
        setTo('')
        setCreatedNumber(null)
        onClose()
    }

    return (
        <Dialog open={open} onClose={close} title={t('invoices.createTitle')} wide>
            {createdNumber ? (
                <div className='space-y-4 py-4 text-center'>
                    <p className='text-4xl'>✓</p>
                    <p className='font-semibold text-slate-800 dark:text-slate-100'>
                        {t('invoices.createSuccess', { number: createdNumber })}
                    </p>
                    <p className='text-sm text-slate-500 dark:text-slate-400'>
                        {t('invoices.createSuccessDesc')}
                    </p>
                    <Button onClick={close}>{t('common.close')}</Button>
                </div>
            ) : (
                <form onSubmit={submit} className='space-y-4'>
                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                        <Field label={t('invoices.client')} className='sm:col-span-2'>
                            <Select
                                value={clientId ?? ''}
                                onChange={e =>
                                    setClientId(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : null,
                                    )
                                }
                                required
                            >
                                <option value=''>{t('invoices.chooseClient')}</option>
                                {(clients.data ?? []).map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                        <Field label={t('invoices.vatPct')}>
                            <Input
                                inputMode='decimal'
                                value={taxRate}
                                onChange={e => setTaxRate(e.target.value)}
                            />
                        </Field>
                    </div>
                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                        <Field label={t('invoices.serviceFrom')}>
                            <DateInput value={from} onChange={setFrom} />
                        </Field>
                        <Field label={t('invoices.serviceTo')}>
                            <DateInput value={to} onChange={setTo} />
                        </Field>
                        <Field label={t('invoices.dueOptional')}>
                            <DateInput value={dueDate} onChange={setDueDate} />
                        </Field>
                    </div>
                    <Field label={t('invoices.noteOptional')}>
                        <Input
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder={t('invoices.notePlaceholder')}
                        />
                    </Field>

                    {clientId !== null && (
                        <div className='rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/60 p-3'>
                            <p className='mb-2 text-xs font-semibold tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                {t('invoices.unbilled')}
                                {from && to
                                    ? ` · ${formatDate(from)} – ${formatDate(to)}`
                                    : from
                                      ? ` · ${t('invoices.unbilledFrom', {
                                            date: formatDate(from),
                                        })}`
                                      : to
                                        ? ` · ${t('invoices.unbilledUntil', {
                                              date: formatDate(to),
                                          })}`
                                        : ''}
                            </p>
                            {unbilled.isLoading ? (
                                <p className='text-sm text-slate-500 dark:text-slate-400'>
                                    {t('invoices.loadingPreview')}
                                </p>
                            ) : unbilled.data && unbilled.data.entryCount > 0 ? (
                                <>
                                    <table className='w-full text-sm'>
                                        <tbody>
                                            {unbilled.data.groups.map(
                                                (g, i) => (
                                                    <tr
                                                        key={i}
                                                        className='border-b border-slate-200/60 last:border-0'
                                                    >
                                                        <td className='py-1.5 text-slate-700 dark:text-slate-200'>
                                                            {g.projectName ??
                                                                t('invoices.noProject')}
                                                        </td>
                                                        <td className='py-1.5 text-right tabular-nums text-slate-500 dark:text-slate-400'>
                                                            {formatDurationCompact(
                                                                g.hours * 3600,
                                                            )}
                                                        </td>
                                                        <td className='py-1.5 text-right tabular-nums text-slate-500 dark:text-slate-400'>
                                                            {formatMoney(
                                                                g.hourlyRate,
                                                            )}
                                                            /h
                                                        </td>
                                                        <td className='py-1.5 pl-3 text-right font-medium tabular-nums text-slate-800 dark:text-slate-100'>
                                                            {formatMoney(
                                                                g.amount,
                                                            )}
                                                        </td>
                                                    </tr>
                                                ),
                                            )}
                                        </tbody>
                                    </table>
                                    <div className='mt-2 flex justify-between border-t border-slate-200 dark:border-slate-700 pt-2 text-sm font-semibold text-slate-700 dark:text-slate-200'>
                                        <span>
                                            {t('invoices.netEntries', {
                                                count: unbilled.data.entryCount,
                                            })}
                                        </span>
                                        <span className='tabular-nums'>
                                            {formatMoney(
                                                unbilled.data.subtotal,
                                            )}
                                        </span>
                                    </div>
                                    {unbilled.data.skippedWithoutRate >
                                        0 && (
                                        <p className='mt-1 text-xs text-amber-600 dark:text-amber-400'>
                                            {t('invoices.skippedWithoutRate', {
                                                count:
                                                    unbilled.data
                                                        .skippedWithoutRate,
                                            })}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className='text-sm text-slate-500 dark:text-slate-400'>
                                    {t('invoices.noUnbilled')}
                                </p>
                            )}
                        </div>
                    )}

                    <div className='flex justify-end gap-2'>
                        <Button type='button' variant='secondary' onClick={close}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type='submit'
                            loading={create.isPending}
                            disabled={
                                !clientId ||
                                !unbilled.data ||
                                unbilled.data.entryCount === 0
                            }
                        >
                            {t('invoices.create')}
                        </Button>
                    </div>
                </form>
            )}
        </Dialog>
    )
}
